import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVendor } from '../context/VendorContext';
import * as api from '../services/api';
import './Home.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4003/api';
const DEFAULT_COUNTRY_IMAGE = 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=400&h=500&fit=crop';

const ALL_PAYMENT_METHODS = [
  { label: 'Pay with Ethereum', icon: '/icons/ethereum.svg', crypto: true },
  { label: 'Pay with Solana', icon: '/icons/solana.svg', crypto: true },
  { label: 'Pay with Bitcoin', icon: '/icons/bitcoin.svg', crypto: true },
  { label: 'Pay with any card', icon: '/icons/card.svg', crypto: false }
];

const SUGGESTIONS = [
  { icon: '🛂', text: 'Apply for a Tourist Visa' },
  { icon: '💼', text: 'Help me setup a remote business' },
];

const Home = ({ user, onShowAuthModal }) => {
  const navigate = useNavigate();
  const { features, branding } = useVendor();
  const PAYMENT_METHODS = features.cryptoPayments
    ? ALL_PAYMENT_METHODS
    : ALL_PAYMENT_METHODS.filter(m => !m.crypto);
  const [chatInput, setChatInput] = useState('');
  const [paymentIndex, setPaymentIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);
  const [visaCards, setVisaCards] = useState([]);
  const [visasLoading, setVisasLoading] = useState(true);
  const [selectedVisa, setSelectedVisa] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [micError, setMicError] = useState(null);
  const chatInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [visaDocuments, setVisaDocuments] = useState([]);

  useEffect(() => {
    const fetchVisas = async () => {
      try {
        const res = await fetch(`${API_URL}/visas/featured`);
        const data = await res.json();
        if (data.success && data.data.length > 0) {
          setVisaCards(data.data.map(v => ({
            id: v.id,
            image: v.country?.image || DEFAULT_COUNTRY_IMAGE,
            alt: v.country?.name,
            flag: v.country?.flag || '',
            label: `${v.country?.name} Visa`,
            visaName: v.visaName,
            maxStay: v.maxStay,
            entriesAllowed: v.entriesAllowed,
            category: v.category?.name || '',
            startingPrice: v.startingPrice,
            validity: v.validity,
            processingTime: v.processingTime,
            country: v.country,
          })));
        }
      } catch (err) {
        console.error('Failed to fetch featured visas:', err);
      } finally {
        setVisasLoading(false);
      }
    };
    fetchVisas();
  }, []);

  const getCardPosition = (index) => {
    const diff = (index - cardIndex + visaCards.length) % visaCards.length;
    if (diff === 0) return 'center';
    if (diff === 1) return 'right';
    if (diff === visaCards.length - 1) return 'left';
    return 'hidden';
  };

  const handlePrev = () => setCardIndex((prev) => (prev - 1 + visaCards.length) % visaCards.length);
  const handleNext = () => setCardIndex((prev) => (prev + 1) % visaCards.length);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setPaymentIndex((prev) => (prev + 1) % PAYMENT_METHODS.length);
        setAnimating(false);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const handleCardClick = async (card) => {
    setSelectedVisa(card);
    setVisaDocuments([]);
    try {
      const res = await fetch(`${API_URL}/visas/${card.id}`);
      const data = await res.json();
      if (data.success && data.data.documents) {
        setVisaDocuments(data.data.documents);
      }
    } catch (err) {
      console.error('Failed to fetch visa details:', err);
    }
  };

  const handleCloseExpanded = () => {
    setSelectedVisa(null);
    setVisaDocuments([]);
  };

  const formatEntries = (entry) => {
    if (entry === 'single') return 'Single entry';
    if (entry === 'double') return 'Double entry';
    return 'Single or multiple entry';
  };

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    navigate('/chat', { state: { type: 'chat-message', message: chatInput.trim() } });
    setChatInput('');
  };

  const handleSuggestionClick = (text) => {
    if (text === 'Apply for a Tourist Visa') {
      setChatInput('Apply for a Tourist Visa to ');
      chatInputRef.current?.focus();
    } else {
      navigate('/chat', { state: { type: 'suggestion', message: text } });
    }
  };

  const handleApplyClick = () => {
    navigate('/chat', { state: { type: 'visa-apply', visa: selectedVisa } });
  };

  // === Voice Recording (STT via Whisper) ===
  const startRecording = useCallback(async () => {
    try {
      setMicError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : undefined;
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        if (blob.size < 1000) {
          setMicError('Recording too short. Please try again.');
          return;
        }

        setIsTranscribing(true);
        try {
          const result = await api.transcribeAudio(blob);
          const text = result.data?.text;
          if (text) {
            navigate('/chat', { state: { type: 'chat-message', message: text } });
          } else {
            setMicError("Couldn't hear anything. Please try again.");
          }
        } catch (err) {
          setMicError(err.message || 'Failed to transcribe audio');
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setMicError('Microphone access denied. Please allow microphone in browser settings.');
      } else {
        setMicError('Could not access microphone.');
      }
    }
  }, [navigate]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, []);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  return (
    <div className="v2-home">
      {/* Hero Section */}
      <section className="hero-section">
        <h1 className="hero-title">
          {branding.tagline || 'The easiest way to live globally.'}
        </h1>

        {!selectedVisa && (
          <div className="payment-badge">
            <img
              src={PAYMENT_METHODS[paymentIndex].icon}
              alt=""
              className={`payment-icon ${animating ? 'slide-out' : 'slide-in'}`}
            />
            <span className={`payment-label ${animating ? 'slide-out' : 'slide-in'}`}>
              {PAYMENT_METHODS[paymentIndex].label}
            </span>
          </div>
        )}

        {selectedVisa ? (
          /* Expanded Visa Detail Card */
          <div className="visa-expanded">
            <div className="visa-expanded-card">
              <button className="visa-expanded-close" onClick={handleCloseExpanded}>
                &#8249; Back
              </button>
              <div className="visa-expanded-image">
                <img src={selectedVisa.image} alt={selectedVisa.alt} loading="lazy" width="180" height="190" onLoad={(e) => { e.target.classList.add('loaded'); }} onError={(e) => { e.target.src = DEFAULT_COUNTRY_IMAGE; }} />
              </div>
              <div className="visa-expanded-details">
                <div className="visa-expanded-header">
                  <span className="visa-expanded-title">
                    {selectedVisa.alt} {selectedVisa.maxStay} - {selectedVisa.category}{selectedVisa.category?.toLowerCase().includes('visa') ? '' : ' Visa'}
                  </span>
                  {selectedVisa.startingPrice != null && (
                    <span className="visa-price-badge">${selectedVisa.startingPrice}</span>
                  )}
                </div>
                <ul className="visa-features">
                  <li><span className="feature-icon">&#10003;</span>{formatEntries(selectedVisa.entriesAllowed)}</li>
                  {selectedVisa.validity && <li><span className="feature-icon">&#10003;</span>Valid for {selectedVisa.validity}</li>}
                  {selectedVisa.processingTime && <li><span className="feature-icon">&#10003;</span>{selectedVisa.processingTime} processing</li>}
                </ul>
                <div className="visa-documents">
                  <span className="visa-documents-label">Docs Required</span>
                  <div className="visa-documents-list">
                    {visaDocuments.length > 0
                      ? visaDocuments.filter(d => d.isMandatory).map((doc, i) => (
                          <span key={i} className="visa-doc-pill">{doc.name}</span>
                        ))
                      : <>
                          <span className="visa-doc-pill">Passport</span>
                          <span className="visa-doc-pill">Photo</span>
                        </>
                    }
                  </div>
                </div>
                <button className="visa-apply-btn" onClick={handleApplyClick}>Apply</button>
              </div>
            </div>
          </div>
        ) : (
          /* Destination Carousel */
          <div className="destination-carousel">
            <div className="carousel-track">
              {visasLoading ? (
                <>
                  <div className="carousel-card card-left skeleton-card" />
                  <div className="carousel-card card-center skeleton-card" />
                  <div className="carousel-card card-right skeleton-card" />
                </>
              ) : (
                <>
                  {visaCards.map((card, i) => {
                    const position = getCardPosition(i);
                    return (
                      <div
                        key={card.id}
                        className={`carousel-card card-${position}`}
                        onClick={position === 'left' ? handlePrev : position === 'right' ? handleNext : position === 'center' ? () => handleCardClick(card) : undefined}
                      >
                        <img src={card.image} alt={card.alt} loading="eager" fetchPriority={position === 'center' ? 'high' : 'auto'} width="170" height="210" onLoad={(e) => { e.target.classList.add('loaded'); }} onError={(e) => { e.target.src = DEFAULT_COUNTRY_IMAGE; }} />
                        {position === 'center' && (
                          <div className="card-badge">
                            <span className="badge-flag">{card.flag}</span>
                            <span>{card.label}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button className="carousel-arrow carousel-arrow-left" onClick={handlePrev}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10.75" fill="#FFFFFF"/><path fillRule="evenodd" clipRule="evenodd" d="M10.9697 16.0303L7.46967 12.5303C7.17678 12.2374 7.17678 11.7626 7.46967 11.4697L10.9697 7.96967C11.2626 7.67678 11.7374 7.67678 12.0303 7.96967C12.3232 8.26256 12.3232 8.73744 12.0303 9.03033L9.81066 11.25H16C16.4142 11.25 16.75 11.5858 16.75 12C16.75 12.4142 16.4142 12.75 16 12.75H9.81066L12.0303 14.9697C12.3232 15.2626 12.3232 15.7374 12.0303 16.0303C11.7374 16.3232 11.2626 16.3232 10.9697 16.0303Z" fill="currentColor"/></svg>
                  </button>
                  <button className="carousel-arrow carousel-arrow-right" onClick={handleNext}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10.75" fill="#FFFFFF"/><path fillRule="evenodd" clipRule="evenodd" d="M13.0303 7.96967L16.5303 11.4697C16.8232 11.7626 16.8232 12.2374 16.5303 12.5303L13.0303 16.0303C12.7374 16.3232 12.2626 16.3232 11.9697 16.0303C11.6768 15.7374 11.6768 15.2626 11.9697 14.9697L14.1893 12.75H8C7.58579 12.75 7.25 12.4142 7.25 12C7.25 11.5858 7.58579 11.25 8 11.25H14.1893L11.9697 9.03033C11.6768 8.73744 11.6768 8.26256 11.9697 7.96967C12.2626 7.67678 12.7374 7.67678 13.0303 7.96967Z" fill="currentColor"/></svg>
                  </button>
                </>
              )}
            </div>

            <p className="carousel-subtitle">{visasLoading ? '' : `${visaCards.length} New Travel visas supported`}</p>
          </div>
        )}
      </section>

      {/* Help Section - hidden when visa is expanded or chat disabled */}
      {!selectedVisa && features.chat && (
        <section className="help-section">
          <h2 className="help-title">What do you need help with?</h2>

          <div className="suggestion-chips">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                className="suggestion-chip"
                onClick={() => handleSuggestionClick(s.text)}
              >
                <span className="chip-icon">{s.icon}</span>
                <span>{s.text}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Chat input - only when chat feature enabled */}
      {features.chat && (
        <form className={`chat-input-wrapper${isRecording ? ' recording' : ''}`} onSubmit={handleChatSubmit}>
          <input
            ref={chatInputRef}
            type="text"
            className="chat-input"
            placeholder={isRecording ? 'Recording... tap mic to stop' : isTranscribing ? 'Transcribing...' : 'What do you need today?'}
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            disabled={isRecording || isTranscribing}
          />
          {!chatInput.trim() && (
            <button
              type="button"
              className={`chat-mic-btn${isRecording ? ' recording' : ''}${isTranscribing ? ' transcribing' : ''}`}
              onClick={toggleRecording}
              disabled={isTranscribing}
            >
              {isTranscribing ? (
                <svg width="18" height="18" viewBox="0 0 24 24" className="chat-mic-spinner">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="1" width="6" height="12" rx="3" />
                  <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              )}
            </button>
          )}
          <button type="submit" className="chat-send-btn" disabled={isRecording || isTranscribing || !chatInput.trim()}>
            <img src="/icons/arrow-send.svg" alt="" className="send-icon" />
          </button>
        </form>
      )}
      {micError && (
        <div style={{ color: '#EF4444', fontSize: '13px', textAlign: 'center', marginTop: '6px' }}>{micError}</div>
      )}
    </div>
  );
};

export default Home;
